import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useChampionship } from "@/context/ChampionshipContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import { Plus, Pencil, Trash2 } from "lucide-react";

const blank = { slug: "", title: "", content_html: "", image_path: "", order: 0, show_in_nav: true };

export default function AdminInfoPagesPage() {
  const { current } = useChampionship() || {};
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = useCallback(async () => {
    if (!current?.id) return;
    const { data } = await api.get(`/championships/${current.id}/info-pages`);
    setItems(data || []);
  }, [current?.id]);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      slug: p.slug, title: p.title, content_html: p.content_html, image_path: p.image_path || "",
      order: p.order || 0, show_in_nav: p.show_in_nav !== false,
    });
    setOpen(true);
  };

  const save = async () => {
    try {
      if (editing) await api.patch(`/info-pages/${editing.id}`, form);
      else await api.post(`/championships/${current.id}/info-pages`, form);
      toast.success("Saved");
      setOpen(false);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this page?")) return;
    try { await api.delete(`/info-pages/${id}`); toast.success("Deleted"); await load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="p-8 lg:p-10" data-testid="admin-info-pages">
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-muted-foreground">Information</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight mt-2">Info pages</h1>
        </div>
        <Button onClick={openNew} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="info-new-button">
          <Plus className="h-4 w-4 mr-2" /> New page
        </Button>
      </div>

      <div className="border border-border bg-white">
        {items.length === 0 ? (
          <div className="p-10 text-center text-muted-foreground">No info pages.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-secondary text-left text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              <tr>
                <th className="p-3">Title</th>
                <th className="p-3">Slug</th>
                <th className="p-3">Order</th>
                <th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((p) => (
                <tr key={p.id} className="border-t border-border" data-testid={`info-row-${p.id}`}>
                  <td className="p-3 font-medium">{p.title}</td>
                  <td className="p-3 text-muted-foreground">/{p.slug}</td>
                  <td className="p-3">{p.order}</td>
                  <td className="p-3 text-right space-x-2">
                    <button onClick={() => openEdit(p)} className="text-primary"><Pencil className="h-4 w-4 inline" /></button>
                    <button onClick={() => remove(p.id)} className="text-destructive"><Trash2 className="h-4 w-4 inline" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit page" : "New page"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="info-title-input" />
              </div>
              <div className="space-y-2">
                <Label>Slug</Label>
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="venue" data-testid="info-slug-input" />
              </div>
            </div>
            <ImageUpload label="Header image" value={form.image_path} onChange={(v) => setForm({ ...form, image_path: v })} />
            <div className="space-y-2">
              <Label>Content (HTML)</Label>
              <Textarea rows={12} value={form.content_html} onChange={(e) => setForm({ ...form, content_html: e.target.value })} data-testid="info-content-input" />
            </div>
            <div className="space-y-2">
              <Label>Order</Label>
              <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-primary text-primary-foreground hover:bg-primary/90" data-testid="info-save-button">Save</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
